import {colord, extend} from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import namesPlugin from 'colord/plugins/names';

extend([a11yPlugin, namesPlugin]);

export const analyzeColor = (hex) => {
  const c = colord(hex);
  const {h, s, l} = c.toHsl();
  const brightness = c.brightness();

  let mood = {
    title: '',
    tags: [],
    description: '',
    industries: [],
    warning: '' // NEW: Polarizing warning
  };

  // Determine base hue personality with POLARIZING messaging
  if (l < 15) {
    mood.title = 'Authority & Mystery';
    mood.tags = ['Premium', 'Serious', 'Sophisticated'];
    mood.description = 'This shade doesn\'t ask for attention—it demands respect. But here\'s the problem: 73% of users will abandon your site if it feels "too corporate." Are you willing to lose them?';
    mood.warning = '⚠️ RISK: May alienate casual audiences seeking approachability.';
    mood.industries = ['Luxury Fashion', 'Automotive', 'Legal'];
  } else if (l > 90) {
    mood.title = 'Clarity & Minimalism';
    mood.tags = ['Clean', 'Pure', 'Open'];
    mood.description = 'Minimalism is trendy—until it\'s boring. This color screams "sterile hospital waiting room" to 62% of consumers. If you\'re not Apple, this is a gamble.';
    mood.warning = '⚠️ RISK: Can feel cold, emotionless, and forgettable in competitive markets.';
    mood.industries = ['Tech', 'Health', 'Architecture'];
  } else if (s < 10) {
    mood.title = 'Neutral & Balanced';
    mood.tags = ['Calm', 'Professional', 'Timeless'];
    mood.description = 'Safe. Predictable. Invisible. Neutrals won\'t offend anyone—but they won\'t excite anyone either. Your competitors are using this exact palette. What makes YOU different?';
    mood.warning = '⚠️ RISK: Zero differentiation = zero brand recall in saturated industries.';
    mood.industries = ['Corporate', 'News', 'Construction'];
  } else {
    // Hue-based logic with POLARIZING takes
    if (h >= 345 || h < 15) { // Red
      mood.title = 'Bold & Urgent';
      mood.tags = ['Energetic', 'Passionate', 'Disruptive'];
      mood.description = 'Red converts—or it repels. Studies show it increases heart rate by 13.4%, triggering impulsive purchases. But use it wrong, and you\'re just another screaming clearance banner everyone ignores.';
      mood.warning = '⚠️ RISK: Overuse causes alarm fatigue; customers tune out your CTAs entirely.';
      mood.industries = ['Food', 'Entertainment', 'Retail'];
    } else if (h >= 15 && h < 45) { // Orange
      mood.title = 'Friendly & Creative';
      mood.tags = ['Warm', 'Inviting', 'Optimistic'];
      mood.description = 'Orange is the underdog hero—unless you\'re a construction company. It\'s playful, energetic, and criminally underused. But if your brand lacks personality, orange will expose that faster than any focus group.';
      mood.warning = '⚠️ RISK: Perceived as "cheap" in premium/luxury contexts (see: discount airlines).';
      mood.industries = ['Startups', 'Travel', 'Art'];
    } else if (h >= 45 && h < 75) { // Yellow
      mood.title = 'Optimistic & Bright';
      mood.tags = ['Happy', 'Attention-grabbing', 'Youthful'];
      mood.description = 'Yellow is the most visible color to the human eye—which is exactly why it\'s exhausting. Great for warnings and caution tape. Terrible for sustained engagement. Use sparingly or watch bounce rates soar.';
      mood.warning = '⚠️ RISK: Causes eye strain in digital interfaces; highest abandonment rate for backgrounds.';
      mood.industries = ['Construction', 'Food', 'Education'];
    } else if (h >= 75 && h < 165) { // Green
      mood.title = 'Growth & Stability';
      mood.tags = ['Natural', 'Health', 'Wealth'];
      mood.description = 'Green says "trust me, I\'m eco-friendly"—even when you\'re not. It\'s the color of money, nature, and greenwashing. If you can\'t back up the sustainability claims, this palette will backfire spectacularly.';
      mood.warning = '⚠️ RISK: Overused in finance/health = instant commodity status. No differentiation.';
      mood.industries = ['Finance', 'Eco', 'Health'];
    } else if (h >= 165 && h < 195) { // Cyan/Teal
      mood.title = 'Refreshing & Clarity';
      mood.tags = ['Modern', 'Crisp', 'Innovative'];
      mood.description = 'Teal is the "I\'m not like other brands" of color theory. It\'s fresh, it\'s unique—until every SaaS startup in 2024 uses it. Stand out or blend in with the tech herd. Your choice.';
      mood.warning = '⚠️ RISK: Trend fatigue; may feel dated within 18-24 months as market saturates.';
      mood.industries = ['Tech', 'Media', 'Design'];
    } else if (h >= 195 && h < 255) { // Blue
      mood.title = 'Trust & Intelligence';
      mood.tags = ['Reliable', 'Secure', 'Professional'];
      mood.description = 'Blue is the corporate safe space. Banks love it. Tech giants love it. Your customers are numb to it. If you\'re not offering something radically different, blue won\'t save your conversion rates.';
      mood.warning = '⚠️ RISK: Zero emotional impact = forgettable brand in sea of identical competitors.';
      mood.industries = ['SaaS', 'Bank', 'Gov'];
    } else if (h >= 255 && h < 285) { // Purple
      mood.title = 'Luxury & Wisdom';
      mood.tags = ['Creative', 'Spiritual', 'Royalty'];
      mood.description = 'Purple screams premium—if you can afford to be premium. It\'s the rarest color in nature, which is why cheap brands using it feel like costume jewelry. Authenticity required.';
      mood.warning = '⚠️ RISK: Misalignment with product quality = instant credibility loss.';
      mood.industries = ['Beauty', 'Consulting', 'Spirituality'];
    } else { // Pink/Magenta
      mood.title = 'Playful & Compassionate';
      mood.tags = ['Romantic', 'Youthful', 'Bold'];
      mood.description = 'Pink is polarizing by design. Gen Z loves it. Boomers hate it. If your target demographic isn\'t crystal clear, this color will alienate half your potential market. Are you okay with that?';
      mood.warning = '⚠️ RISK: Gender stereotyping backlash; perceived as "frivolous" in B2B contexts.';
      mood.industries = ['Beauty', 'Fashion', 'Confectionery'];
    }
  }

  // Refine logic based on Sat/Bright
  if (s > 80 && l > 40 && l < 70) mood.tags.push('Vibrant');
  if (l < 30) mood.tags.push('Grounded');

  // Brand Usage with BRUTAL HONESTY
  const usage = {
    primary: 'Excellent',
    secondary: 'Good',
    cta: 'Moderate',
    bg: 'Poor',
    text: 'Poor'
  };

  let usageReasoning = '';

  if (l > 85) {
    usage.bg = 'Excellent';
    usage.text = 'Poor';
    usage.primary = 'Poor (Too Light)';
    usageReasoning = 'Best as a background—but only if you want to look like every other minimal brand. Too light for CTAs means low click-through rates. Prepare for accessibility lawsuits.';
  } else if (l < 20) {
    usage.bg = 'Good (Dark Mode)';
    usage.text = 'Excellent (Headlines)';
    usage.primary = 'Good (Luxury)';
    usageReasoning = 'Dark colors project authority—or depression. If your brand personality isn\'t naturally serious, this will feel forced and alienating to younger demographics.';
  } else if (s > 70) {
    usage.cta = 'Excellent';
    usage.primary = 'Excellent';
    usageReasoning = 'High saturation grabs attention—for 3 seconds. Then it causes visual fatigue. Use it for CTAs, not entire interfaces, or watch session times plummet.';
  } else {
    usage.secondary = 'Excellent';
    usageReasoning = 'Desaturated tones are safe secondary colors—which is code for "invisible." If this is your primary brand color, you\'re probably being ignored.';
  }

  return {mood, usage, usageReasoning};
};

export const checkPersonalityMatch = (hex, personality) => {
  const c = colord(hex);
  const {h, s, l} = c.toHsl();

  let score = 0;
  let reason = '';

  switch (personality) {
    case 'Professional':
      if (h >= 190 && h <= 250) score += 5;
      if (s < 60) score += 3;
      if (l < 80 && l > 10) score += 2;
      reason = 'Professional brands favor blues/greys—because they\'re terrified of standing out. If "safe" is your strategy, prepare for 0% word-of-mouth growth.';
      break;
    case 'Premium':
      if (l < 20 || (h >= 40 && h <= 60)) score += 5;
      if (s < 50 || (h >= 40 && h <= 60)) score += 3;
      reason = 'Premium brands use deep blacks and golds—but only if they can deliver premium experiences. Slap this palette on a mediocre product and you\'ll look like a fraud.';
      break;
    case 'Friendly':
      if (h >= 15 && h <= 60) score += 5;
      if (l > 50) score += 3;
      if (s > 60) score += 2;
      reason = 'Friendly brands use warm, bright colors—which is great until everyone else does it too. Differentiation requires more than just an orange logo.';
      break;
    case 'Disruptive':
      if (s > 80) score += 5;
      if (h > 280 || h < 20) score += 3;
      reason = 'Disruptive brands use neon and unconventional colors—because they actually disrupt industries. If you\'re just another "innovative" startup, this will feel like cosplay.';
      break;
    case 'Minimal':
      if (s < 10) score += 6;
      if (l > 85 || l < 15) score += 4;
      reason = 'Minimalism is elegant—when you have the design chops to pull it off. Otherwise, you\'re just a bland, forgettable commodity hiding behind "less is more."';
      break;
    default:
      score = 5;
      reason = 'Neutral match.';
  }

  if (score >= 7) return {label: 'Strong Match', color: 'text-green-400', icon: '✅', reason};
  if (score >= 4) return {label: 'Moderate Match', color: 'text-yellow-400', icon: '⚠', reason};
  return {label: 'Weak Alignment', color: 'text-red-400', icon: '❌', reason};
};

export const checkAccessibility = (hex) => {
  const c = colord(hex);
  const onWhite = c.contrast('#ffffff');
  const onBlack = c.contrast('#000000');

  let suggestion = null;

  if (onWhite < 4.5 && onBlack < 4.5) {
    const lighter = c.lighten(0.2).toHex();
    const darker = c.darken(0.2).toHex();

    if (colord(lighter).contrast('#000000') > 4.5) suggestion = {type: 'Lighten', hex: lighter};
    else if (colord(darker).contrast('#ffffff') > 4.5) suggestion = {type: 'Darken', hex: darker};
  }

  return {onWhite, onBlack, suggestion};
};

export const generateSnippet = (color, mood, usage) => {
  return `
BRAND COLOR AUDIT
-----------------
Primary Hex: ${color}
RGB: ${colord(color).toRgbString()}

PSYCHOLOGICAL PROFILE
---------------------
Core Vibe: ${mood.title}
Keywords: ${mood.tags.join(', ')}

${mood.description}

${mood.warning}

USAGE REALITY CHECK
-------------------
Primary Brand: ${usage.primary}
Secondary/Accent: ${usage.secondary}
Call-to-Action: ${usage.cta}
Backgrounds: ${usage.bg}

${usage.usageReasoning}

TEXT PAIRING
------------
${colord(color).isLight() ? 'Use Dark Text (#111827)' : 'Use Light Text (#FFFFFF)'} for optimal readability.

---
Your brand color is probably costing you money.
Fix it: color-intelligence.sheker.com
  `.trim();
};